import React from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import Button from '../../../../../components/button';
import {
  Datepicker,
  Form as CustomForm,
  Input,
} from '../../../../../components/form';
import { useToasts } from '../../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { toISODateOnly } from '../../../../../utils/dateUtils';
import { parseISO } from 'date-fns';
import {
  useCreateMyEducationMutation,
  useUpdateMyEducationMutation,
} from '../../../__generated__/graphql';

export type EducationRecord = {
  id: string;
  name: string;
  school: string | null;
  degree: string | null;
  location: string | null;
  startDate: string | null;
  endDate: string | null;
  description: string | null;
};

type Props = {
  show: boolean;
  onClose: () => void;
  onSave: () => void;
  education: EducationRecord | null;
};

type FormData = {
  school: string;
  degree: string;
  location: string;
  startDate: Date | null;
  endDate: Date | null;
  description: string;
};

const formSchema: yup.ObjectSchema<FormData> = yup.object({
  school: yup.string().defined(),
  degree: yup.string().required(),
  location: yup.string().defined(),
  startDate: yup.date().nullable().defined(),
  endDate: yup.date().nullable().defined(),
  description: yup.string().defined(),
});

export const EducationSaveModal: React.FC<React.PropsWithChildren<Props>> = ({
  show,
  onClose,
  onSave,
  education,
}) => {
  const { t } = useTranslation();
  const { addSuccess } = useToasts();
  const errorHandler = useMutationErrorHandler();

  const [createEducation, { loading: createLoading }] =
    useCreateMyEducationMutation();
  const [updateEducation, { loading: updateLoading }] =
    useUpdateMyEducationMutation();

  const defaultValues: FormData = {
    school: education?.school ?? '',
    degree: education?.degree ?? '',
    location: education?.location ?? '',
    startDate: education?.startDate ? parseISO(education.startDate) : null,
    endDate: education?.endDate ? parseISO(education.endDate) : null,
    description: education?.description ?? '',
  };

  const handleSubmit = async (formData: FormData) => {
    const input = {
      name: formData.degree.slice(0, 80),
      school: formData.school || null,
      degree: formData.degree,
      location: formData.location || null,
      startDate: formData.startDate ? toISODateOnly(formData.startDate) : null,
      endDate: formData.endDate ? toISODateOnly(formData.endDate) : null,
      description: formData.description || null,
    };

    if (education) {
      await updateEducation({
        variables: { input: { id: education.id, ...input } },
      })
        .then(() => {
          addSuccess(t('personalData.education.updateSuccess'));
          onSave();
        })
        .catch(errorHandler);
    } else {
      await createEducation({
        variables: { input },
      })
        .then(() => {
          addSuccess(t('personalData.education.createSuccess'));
          onSave();
        })
        .catch(errorHandler);
    }
  };

  return (
    <Modal enforceFocus={false} show={show} centered onHide={onClose} size="lg">
      <Modal.Header closeButton className="card-header">
        <h4 className="card-header-title">
          {education
            ? t('personalData.education.editTitle')
            : t('personalData.education.createTitle')}
        </h4>
      </Modal.Header>
      <CustomForm
        onSubmit={handleSubmit}
        validationSchema={formSchema}
        defaultValues={defaultValues}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Input
                name="degree"
                label={t('personalData.education.fields.degree')}
                required
              />
            </Col>
            <Col md={6}>
              <Input
                name="school"
                label={t('personalData.education.fields.school')}
              />
            </Col>
            <Col md={6}>
              <Input
                name="location"
                label={t('personalData.education.fields.location')}
              />
            </Col>
            <Col md={6}>
              <Datepicker
                name="startDate"
                label={t('personalData.education.fields.startDate')}
              />
            </Col>
            <Col md={6}>
              <Datepicker
                name="endDate"
                label={t('personalData.education.fields.endDate')}
              />
            </Col>
            <Col md={12}>
              <Input
                name="description"
                label={t('personalData.education.fields.description')}
                type="textarea"
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex">
            <Button
              label={t('form.general.cancelButton')}
              size="sm"
              className="mr-2 me-2"
              variant="outline-secondary"
              onClick={onClose}
            />
            <Button
              label={t('form.general.confirmButton')}
              size="sm"
              variant="primary"
              type="submit"
              isProcessing={createLoading || updateLoading}
            />
          </div>
        </Modal.Footer>
      </CustomForm>
    </Modal>
  );
};
